/* eslint-disable no-param-reassign */
import { PinIcon } from '@eleveo/icons-react';
import { differenceInHours, differenceInMinutes, format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import Highcharts, { Options as HighchartsOptions, Point, Series, SeriesGanttOptions, XAxisOptions } from 'highcharts';
import DraggablePointsModule from 'highcharts/modules/draggable-points';
import highchartsGantt from 'highcharts/modules/gantt';
import PatternFillModule from 'highcharts/modules/pattern-fill';
import HighchartsReact from 'highcharts-react-official';
import cloneDeep from 'lodash.clonedeep';
import { isEmpty } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../../app/AuthorizationProvider';
import { getZonedDateTimezoneOffset } from '../../../../utils/date';
import { CHART_HOUR_INTERVAL_WIDTH, CHART_OUT_OF_RANGE_COLOR } from '../../config';
import { ChartScheduleElement, SchedulingGanttChartDragState } from '../../types';
import { differenceInValues } from '../../utils/mapChartData';

highchartsGantt(Highcharts);
DraggablePointsModule(Highcharts);
PatternFillModule(Highcharts);

const PADDING = 8;
const WIDTH_OF_LABEL = 6;
const HEIGHT_OF_LABEL = 16;

export interface GanttChartProps {
  containerRef: React.RefObject<HTMLDivElement>;
  chartOptions: HighchartsOptions;
  chartRef: React.RefObject<HighchartsReact>;
  draggedState: SchedulingGanttChartDragState;
  scheduleTimezone: string;
  viewedTimezone: string;
  wholeScheduleEnd: number;
  wholeScheduleStart: number;
}

export const GanttChart: React.FC<GanttChartProps> = ({
  containerRef,
  chartOptions,
  chartRef,
  draggedState,
  scheduleTimezone,
  viewedTimezone,
  wholeScheduleEnd,
  wholeScheduleStart,
}) => {
  const { localeObj } = useAuth();
  const { t } = useTranslation();

  const wholeScheduleStartUTC = zonedTimeToUtc(wholeScheduleStart, scheduleTimezone).getTime();
  const wholeScheduleEndUTC = zonedTimeToUtc(wholeScheduleEnd, scheduleTimezone).getTime();

  useEffect(() => {
    /**
     * when viewed timezone is not equal to schedule's one, the first and the very last day
     * are expected to contain out of range areas, which need to be highlighted to the user
     * (so we'd render gray-ish background including label, which should be in the center of that area)
     */
    let elementGroupOnStart: Highcharts.SVGElement | undefined;
    let elementGroupOnEnd: Highcharts.SVGElement | undefined;
    let startLabel: Highcharts.SVGElement | undefined;
    let endLabel: Highcharts.SVGElement | undefined;

    if (chartRef.current) {
      const xAxisMax = (chartOptions.xAxis as XAxisOptions[])[0].max || 0;
      const xAxisMin = (chartOptions.xAxis as XAxisOptions[])[0].min || 0;

      const { chartHeight } = chartRef.current.chart;
      const marginBottom = chartRef.current.chart.userOptions.chart?.marginBottom ?? 0;
      const options = {
        fill: CHART_OUT_OF_RANGE_COLOR,
      };
      const height = chartHeight - marginBottom + 1;

      if (xAxisMin < wholeScheduleStartUTC) {
        // out of schedule area at the beginning of first day in schedule range
        const offsetInHours = (wholeScheduleStartUTC - xAxisMin) / 1000 / 60 / 60;
        const width = offsetInHours * CHART_HOUR_INTERVAL_WIDTH;

        elementGroupOnStart = chartRef.current.chart.renderer.g('out-of-range-area');

        const backgroundElement = chartRef.current.chart.renderer.rect(0, 0, width, height).add(elementGroupOnStart);
        backgroundElement.attr(options);

        const lengthOfText = t('schedule.outOfRange').length * WIDTH_OF_LABEL;
        const countOfLines = Math.ceil(lengthOfText / width);
        const paddingForWidth = countOfLines > 1 ? PADDING : 0;

        const x = Math.max((width - lengthOfText) / 2 + paddingForWidth, 0);
        const y = height / 2 - (countOfLines * HEIGHT_OF_LABEL + PADDING) / 2;

        startLabel = chartRef.current.chart.renderer
          .label(t('schedule.outOfRange'), x, y, undefined, undefined, undefined, true)
          .add();

        elementGroupOnStart.add();
      } else if (xAxisMax > wholeScheduleEndUTC) {
        // out of schedule area at the end of last day in schedule range
        const offsetInHours = differenceInHours(xAxisMax, wholeScheduleEndUTC);
        const startXPosition = (24 - offsetInHours) * CHART_HOUR_INTERVAL_WIDTH;
        const width = offsetInHours * CHART_HOUR_INTERVAL_WIDTH;

        elementGroupOnEnd = chartRef.current.chart.renderer.g('out-of-range-area');

        const backgroundElement = chartRef.current.chart.renderer
          .rect(startXPosition, 0, chartRef.current.chart.chartWidth, height)
          .add(elementGroupOnEnd);
        backgroundElement.attr(options);

        const lengthOfText = t('schedule.outOfRange').length * WIDTH_OF_LABEL;
        const countOfLines = Math.ceil(lengthOfText / width);
        const paddingForWidth = countOfLines > 1 ? PADDING : 0;

        const x = startXPosition + Math.max((width - lengthOfText) / 2 + paddingForWidth, 0);
        const y = height / 2 - (countOfLines * HEIGHT_OF_LABEL + PADDING) / 2;

        endLabel = chartRef.current.chart.renderer
          .label(t('schedule.outOfRange'), x, y, undefined, undefined, undefined, true)
          .add();

        elementGroupOnEnd.add();
      }
    }

    // prevent jumping of the window when re-rendering the chart
    if (containerRef.current) {
      containerRef.current.style.minHeight = '';
      containerRef.current.style.minHeight = window.getComputedStyle(containerRef.current).height;
    }

    return () => {
      elementGroupOnEnd?.destroy();
      endLabel?.destroy();
      elementGroupOnStart?.destroy();
      startLabel?.destroy();
    };
  });

  const options: HighchartsOptions = useMemo(
    () => ({
      ...chartOptions,
      time: {
        getTimezoneOffset: (timestamp: number) =>
          getZonedDateTimezoneOffset(utcToZonedTime(timestamp, viewedTimezone), viewedTimezone),
        useUTC: true,
      },
      tooltip: {
        ...chartOptions.tooltip,
        formatter() {
          /* eslint-disable react/no-this-in-sfc */
          const point = this.point as Point & ChartScheduleElement;
          // @ts-expect-error: incompatible type 😥
          const color = typeof this.color === 'string' ? this.color : point.defaultColor;
          const pinnedIcon = point.pinned ? renderToStaticMarkup(<PinIcon className="ml-1" style={{ color }} />) : '';
          const zonedStartDate = utcToZonedTime(point.start, viewedTimezone);
          const zonedEndDate = utcToZonedTime(point.end, viewedTimezone);
          const startTime = format(zonedStartDate, t('date.formats.timeShort'), { locale: localeObj });
          const endTime = format(zonedEndDate, t('date.formats.timeShort'), { locale: localeObj });
          const durationInMinutesTotal = differenceInMinutes(point.end, point.start);
          const durationInHours = differenceInHours(point.end, point.start);
          const durationInMinutes = durationInMinutesTotal - durationInHours * 60;
          const durationInHoursLabel = durationInHours ? `${durationInHours} ${t('time.hrs')}` : '';
          const durationInMinutesLabel = durationInMinutes ? `${durationInMinutes} ${t('time.min')}` : '';
          const productive = t(`schedule.element.${point.productive ? 'productive' : 'nonProductive'}`);
          const paid = t(`schedule.element.${point.paid ? 'paid' : 'unpaid'}`);
          return (
            `<div class="highcharts-tooltip-inner" style="border-color: ${color}">` +
            `<p><label>${t('schedule.element.element')}:</label><br><b>${
              point.scheduleElementName
            }</b>${pinnedIcon}</p>` +
            `<p><label>${t('time.duration')}: <b>${startTime} - ${endTime}</b></label><br>` +
            `<b><big>${durationInHoursLabel} ${durationInMinutesLabel}</big></b></p>` +
            `<p><label>${t('layout.type')}:</label><br><b>${productive} - ${paid}</b></p>` +
            `</div>`
          );
        },
      },
    }),
    [chartOptions, t, viewedTimezone, localeObj]
  );

  useEffect(() => {
    if (chartRef?.current?.chart) {
      const { chart } = chartRef.current;

      // Do not compare series data at all, as they are manually set on every render.
      const clonedOptions = cloneDeep(options);
      (clonedOptions?.series as SeriesGanttOptions[])?.forEach((series) => delete series.data);

      const toUpdate = differenceInValues(chart.options, clonedOptions);

      // update series data manually!
      (options.series as SeriesGanttOptions[])?.forEach((series, index) => {
        // only 0th series could contain dragged elements
        // update dragged points only if they are not colliding with other elements!
        if (index === 0 && draggedState.shouldAllowDrop) {
          // this field should contain last dragged points before update
          const draggedPointsRecord = cloneDeep((chart as any).dragDropData?.newPoints);
          if (draggedPointsRecord && !isEmpty(draggedPointsRecord)) {
            series.data = series.data?.map((point) => {
              // @ts-expect-error: incompatible type 😥
              const pointId = point.id;

              if (draggedPointsRecord[pointId]) {
                // point should be updated with its previous position
                return {
                  ...point,
                  start: draggedPointsRecord[pointId].newValues.start ?? draggedPointsRecord[pointId].point.start,
                  end: draggedPointsRecord[pointId].newValues.end ?? draggedPointsRecord[pointId].point.end,
                  y: draggedPointsRecord[pointId].newValues.y ?? draggedPointsRecord[pointId].point.y,
                };
              }

              return point;
            });
          }
        }

        // @ts-expect-error: incompatible type 😥
        chart.series[index].setData(series.data, false);
      });

      // @ts-expect-error: incompatible type 😥
      const hoveredPointId = chart.hoverPoint?.id;
      const { hoverSeries } = chart;

      if (toUpdate && !isEmpty(toUpdate)) {
        // disable animation on update
        chart.update(cloneDeep(toUpdate), false, true, false);
      }

      chart.redraw(false);

      // restore hovered point & series after redraw so that user could interact with previously hovered point
      if (hoveredPointId) {
        chart.hoverPoint = chart.get(hoveredPointId) as Point;
      }
      if (hoverSeries) {
        chart.hoverSeries = hoverSeries;
      }

      const dataSeries = chartRef.current.chart.get('schedule-elements') as Series;
      if (dataSeries) {
        dataSeries.points.forEach((point: Point) => {
          // @ts-expect-error: incompatible type 😥
          if (point.selected !== null || point.id !== null) {
            // check null or undefined, not false or 0!
            // removed elements will have every property set as `null`, therefore check first than update
            if (point.selected) {
              // @ts-expect-error: incompatible type 😥
              (point.graphic.element as SVGGElement).classList.add('highcharts-point-select');
            } else {
              // @ts-expect-error: incompatible type 😥
              (point.graphic.element as SVGGElement).classList.remove('highcharts-point-select');
            }
          }
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, chartRef]);

  return (
    <HighchartsReact
      ref={chartRef}
      highcharts={Highcharts}
      options={!chartRef?.current?.chart ? options : {}}
      constructorType="ganttChart"
    />
  );
};
